import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "../../styles/HomeStyling/LoginForm.css";
import { ChangePassword } from "./ChangePassword";
import { ForgotPassword } from "./ForgotPassword";
import RequestForm from "./RequestForm";

export const LoginForm = ({ setIsLoggedIn, setUserData, closeForm }) => {
  const lwcLogo = `${process.env.PUBLIC_URL}/assets/images/Live_Well_Collaborative_Logo.png`;
  const elasticSearch = `${process.env.PUBLIC_URL}/assets/images/ElasticSearch_Logo.png`;
  const Back = `${process.env.PUBLIC_URL}/assets/logos/back.svg`;


  const navigate = useNavigate();

  // State for login inputs
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);


  // State for OTP
  const [otp, setOtp] = useState(Array(6).fill(""));
  const otpRefs = useRef([]);

  // Additional UI states
  const [requireOtp, setRequireOtp] = useState(false);
  const [error, setError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isButtonActive, setIsButtonActive] = useState(false);
  const [timer, setTimer] = useState(0);
  const [requestOtpError, setRequestOtpError] = useState("");
  const [requestOtpSuccess, setRequestOtpSuccess] = useState("");
  const [initialDisable, setInitialDisable] = useState(true);

  const [showRequestForm, setShowRequestForm] = useState(false);


  // Enable login button only when email and password are provided
  useEffect(() => {
    setIsButtonActive(email.trim() !== "" && password.trim() !== "");
  }, [email, password]);

  // When OTP is required, start a 20-second disable period for requesting a new OTP
  useEffect(() => {
    if (requireOtp) {
      setInitialDisable(true);
      const disableTimeout = setTimeout(() => {
        setInitialDisable(false);
      }, 20000);
      return () => clearTimeout(disableTimeout);
    }
  }, [requireOtp]);

  // Countdown timer for requesting new OTP
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  /* ------------------------------------------------------------------
   * Login
   * ------------------------------------------------------------------ */
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/login`,
        { email, password },
        { withCredentials: true }
      );

      if (response.data.success) {
        if (response.data.firstTime) {
          setShowChangePassword(true);
          return;
        }
        if (response.data.require_otp) {
          setRequireOtp(true);
        } else {
          setError("Unexpected response from the server. Please try again.");
        }
      } else {
        setError(
          response.data.message || "Invalid credentials. Please try again."
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Network error. Please check your connection."
      );
    }  finally {
      setIsSubmitting(false); // End submission regardless of outcome
    }
  };

  /* ------------------------------------------------------------------
   * OTP Verification
   * ------------------------------------------------------------------ */
  const handleVerifyOtp = async () => {
    setOtpError("");

    try {
      const joinedOtp = otp.join("");

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/verify_otp`,
        { email, otp: joinedOtp },
        { withCredentials: true }
      );

      if (response.data.success) {
        const authResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/check_auth`,
          { withCredentials: true }
        );
        if (authResponse.data.success) {
          setUserData(authResponse.data.user);
          setIsLoggedIn(true);
          closeForm();
          navigate("/dashboard");
        }
      } else {
        setOtpError(response.data.message || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      setOtpError(
        err.response?.data?.message || "OTP verification failed. Try again."
      );
    }
  };

  const handleOtpChange = (e, index) => {
    const newOtp = [...otp];
    newOtp[index] = e.target.value.replace(/\D/, ""); // allow only digits
    setOtp(newOtp);

    // Move to the next input if the current one is filled (and not the last)
    if (e.target.value && index < 5) {
      otpRefs.current[index + 1].focus();
    }

    // // If all 6 digits are filled, automatically verify
    // if (!newOtp.includes("") && newOtp.join("").length === 6) {
    //   handleVerifyOtp();
    // }
  };

  /**
   * If backspace on an empty slot, move focus to the previous field.
   */
  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
    if (e.key === "Enter") {
      handleVerifyOtp();
    }
  };

  /**
   * Pasting the entire 6-digit OTP into any slot
   * will distribute those 6 digits (or however many are in the clipboard)
   * across the inputs starting at that slot index.
   */
  const handleOtpPaste = (e, index) => {
    e.preventDefault();

    // Get only the digits from the clipboard
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6); // limit to 6 digits in case there's more

    if (!pastedData) return;

    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      const slotIndex = index + i;
      if (slotIndex < 6) {
        newOtp[slotIndex] = pastedData[i];
      }
    }
    setOtp(newOtp);

    // Optionally, focus the next unfilled slot or the last if it's fully filled
    const nextUnfilledIndex = newOtp.findIndex((val) => val === "");
    if (nextUnfilledIndex !== -1) {
      otpRefs.current[nextUnfilledIndex].focus();
    }
    // } else {
    //   // All slots are filled => auto verify
    //   handleVerifyOtp();
    // }
  };

  /* ------------------------------------------------------------------
   * Request New OTP
   * ------------------------------------------------------------------ */
  const handleRequestNewOtp = async () => {
    setRequestOtpError("");
    setRequestOtpSuccess("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/request_otp`,
        { email },
        { withCredentials: true }
      );
      if (response.data.success) {
        setRequestOtpSuccess("New OTP sent successfully.");
        setTimer(60);
      } else {
        setRequestOtpError(
          response.data.message || "Failed to send new OTP. Please try again."
        );
      }
    } catch (err) {
      setRequestOtpError(
        err.response?.data?.message || "Network error. Please try again."
      );
    }
  };

  /* ------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------ */
  return (
    <div className="login-popup-container">
      <div className="logos-container">
        <div className="logo-wrapper">
          <img src={lwcLogo} alt="LWC Logo" className="logo-image" />
        </div>
        <div className="logo-wrapper">
          <img
            src={elasticSearch}
            alt="ElasticSearch Logo"
            className="logo-image"
          />
        </div>
      </div>
      {/* { !showChangePassword && !requireOtp && !showForgotPassword && (
        <div style={{ width: "100%", display: "flex", justifyContent: "flex-start" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              width: "fit-content",
              borderRadius: "50px",
              backgroundColor: "#eaeaea",
              padding: "10px",
            }}
          >
            <img src={Back} alt="Back to Website" onClick={() => (window.location.href = 'https://www.livewellcollaborative.org')} />
          </div>
        </div>
      )} */}

      {showForgotPassword ? (
        <ForgotPassword onBack={() => setShowForgotPassword(false)} />
      ) : (
        <>
          {!showChangePassword ? (
            !requireOtp ? (
              <>
                <form className="login-form-container" onSubmit={handleLogin}>
                  <h2 className="login-title">Log In</h2>
                  {error && <p className="login-error-message">{error}</p>}

                  <div className="login-form-group">
                    <label className="login-label">Email</label>
                    <input
                      type="email"
                      className="login-input"
                      value={email}
                      placeholder="Enter Email"
                      aria-label="Enter your Email Address"
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="login-form-group">
                    <label className="login-label">Password</label>
                    <div className="login-input-container">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="login-input"
                        value={password}
                        placeholder="Enter Password"
                        aria-label="Enter your Password"
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      {password.length !== 0 && (
                        <span
                          className="eye-icon"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label="Toggle Password Visibility"
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="forgot-password-container">
                    <button
                      type="button"
                      className="forgot-password-link"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot Password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    className={`login-submit-btn ${
                      isButtonActive ? "login-active" : ""
                    }`}
                    disabled={!isButtonActive || isSubmitting}
                  >
                    {isSubmitting ? "Signing in..." : "Sign In"}
                  </button>
                  <div className="request-access-link">
                    <p>Don't have an account?</p>
                    <p
                      className="forgot-password-link"
                      style={{fontSize: "12px", fontFamily: "Open Sans, sans-serif", fontWeight: 500 }}
                      onClick={() => setShowRequestForm(true)}
                    >
                      Request Access
                    </p>
                  </div>
                  
                </form>
                {showRequestForm && <RequestForm onClose={() => setShowRequestForm(false)} />}
              </>
            ) : (
              <div className="otp-container">
                <div style={{width: "100%"}}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      width: "fit-content",
                      borderRadius: "50px",
                      backgroundColor: "#eaeaea",
                      padding: "10px",
                    }}
                  >
                    <img src={Back} alt="Back to Login" onClick={() => {setRequireOtp(false)}} />
                  </div>
                </div>
                <h2 className="login-title">Verify Your One-Time Passcode</h2>
                {otpError && <p className="login-error-message">{otpError}</p>}
                <p className="otp-subtext">
                  Enter the 6-digit OTP sent to your email!
                </p>

                <div className="otp-form-group">
                  {Array(6)
                    .fill()
                    .map((_, index) => (
                      <input
                        key={index}
                        type="text"
                        maxLength="1"
                        className="otp-slot"
                        value={otp[index] || ""}
                        onChange={(e) => handleOtpChange(e, index)}
                        onKeyDown={(e) => handleOtpKeyDown(e, index)}
                        onPaste={(e) => handleOtpPaste(e, index)}
                        ref={(el) => (otpRefs.current[index] = el)}
                      />
                    ))}
                </div>

                <button
                  type="submit"
                  onClick={handleVerifyOtp}
                  className="login-submit-btn"
                >
                  Verify OTP
                </button>

                <div className="request-otp-container">
                  <p className="request-otp-text">Didn't receive an email?</p>
                  {requestOtpError && (
                    <p className="request-otp-error">{requestOtpError}</p>
                  )}
                  {requestOtpSuccess && (
                    <p className="request-otp-success">{requestOtpSuccess}</p>
                  )}
                  <button
                    onClick={handleRequestNewOtp}
                    className="new-otp-submit-btn"
                    disabled={timer > 0}
                  >
                    {timer > 0
                      ? `Request New OTP (${timer}s)`
                      : "Request New OTP"}
                  </button>
                </div>
              </div>
            )
          ) : (
            <ChangePassword
              email={email}
              setShowChangePassword={setShowChangePassword}
              onBack={() => {setShowChangePassword(false);}}
            />
          )}
        </>
      )}

      <div className="login-description">
        <h2 className="login-description-title">
          What Is the Live Well Elastic Search?
        </h2>
        <p className="login-description-text">
          The Live Well Collaborative Elastic Search is a project explorer which
          provides access to all of the rich insights, project work, and themes
          Live Well teams have completed.
        </p>
        <p className="login-description-text">
          This tool is for new co-ops to learn and leverage work from past
          projects, current team members to connect information to current
          projects, and project sponsors to reference. Take a look around!
        </p>
      </div>
    </div>
  );
};
